import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '../../../../common/element/Dialog';
import Tabs from '../../../../common/element/Tabs/Tabs';
import TabList from '../../../../common/element/Tabs/TabList';
import TabNav from '../../../../common/element/Tabs/TabNav';
import TabContent from '../../../../common/element/Tabs/TabContent';
import BtnPrimary from '../../../../common/element/Buttons/btnPrimary';
import Select from '../../../../common/element/Select';
import ThreeDotLoader from "../../../../common/element/ButtonLoader/ThreeDotLoader";
import CommonPhoneInput from '../../../../common/CommonPhoneInput/CommonPhoneInput';
import SweetAlertService from "../../../../common/SweetAlert/SweetAlert";

// Redux Imports
import { clearCities, clearStates, getCityList, getCountryList, getStateList } from '../../../../redux/countryStateCity/cityStateCountrySlice';
import { selectCityList, selectCountryList, selectStateList } from '../../../../redux/countryStateCity/cityStateCountrySelector';
import { formatToolName } from '../../../../common/Formatters.js/formatters';
import { selectToolListData } from '../../../../redux/SessionMaster/sessionMasterSelector';
import { Toollist } from '../../../../redux/SessionMaster/sessionMasterSlice';
import {
    createCompanyAdhoc,
    editCompanyAdhoc,
} from '../../../../redux/AdhocCompany/Companyadhocslice';

const AddEditAdhocCompany = ({ dialogOpen, onDialogClose, individualData, isEdit }) => {
    const [tab, setTab] = useState("tab1");
    const dispatch = useDispatch();

    const countryList = useSelector(selectCountryList);
    const stateList = useSelector(selectStateList);
    const cityList = useSelector(selectCityList);
    const toolData = useSelector(selectToolListData);

    // --- Options Preparation ---
    const toolOptions = useMemo(() => {
        return (toolData || []).map((item) => ({
            value: item.id || item._id || item.tool_id,
            label: formatToolName(item.tool || item.tool_name),
        }));
    }, [toolData]);

    const countryOptions = countryList.map(country => ({
        value: country.country_id.toString(),
        label: country.country_name
    }));

    const stateOptions = stateList.map(state => ({
        value: state.state_id.toString(),
        label: state.state_name
    }));

    const cityOptions = cityList.map(city => ({
        value: city.city_id.toString(),
        label: city.city_name
    }));

    // Fetch initial master data
    useEffect(() => {
        dispatch(getCountryList());
        dispatch(Toollist());
    }, [dispatch]);

    // **Prefill Logic for Edit Mode**
    useEffect(() => {
        if (isEdit && individualData && dialogOpen) {
            if (individualData.country?.country_id) {
                dispatch(getStateList(individualData.country.country_id));
            }
            if (individualData.state?.state_id) {
                dispatch(getCityList(individualData.state.state_id));
            }
        }
    }, [isEdit, individualData, dialogOpen, dispatch]);

    const splitAddress = (address) => {
        if (!address) return { line1: "", line2: "" };
        const lines = address.split(/\r?\n/);
        return {
            line1: lines[0] || "",
            line2: lines.slice(1).join('\n') || ""
        };
    };

    const getInitialValues = () => {
        const baseValues = {
            company_name: "", 
            contact_person: "", 
            contact_email: "", 
            contact_phone: "",
            first_name: "", 
            last_name: "", 
            email: "", 
            mobile_number: "",
            address: "", 
            addressLine2: "", 
            city: null, 
            state: null, 
            pincode: "", 
            country: null,
            quota: "", 
            tool: [],
            // Helper fields for validation
            contact_phone_country: null, 
            mobile_number_country: null,
        };

        if (isEdit && individualData) {
            const addressParts = splitAddress(individualData.address);
            let selectedTools = [];
            if (Array.isArray(individualData.tools)) {
                selectedTools = individualData.tools.map(tool => ({
                    value: tool.tool_id,
                    label: formatToolName(tool.tool_name),
                }));
            }
            
            return {
                company_name: individualData.company_name || "",
                contact_person: individualData.contact_person || "",
                contact_email: individualData.contact_email || "",
                contact_phone: individualData.contact_phone || "",
                first_name: individualData.hr?.first_name || "",
                last_name: individualData.hr?.last_name || "",
                email: individualData.hr?.email || "",
                mobile_number: individualData.hr?.mobile_number || "",
                address: addressParts.line1,
                addressLine2: addressParts.line2,
                pincode: individualData.pincode || "",
                country: individualData.country ? { value: individualData.country.country_id.toString(), label: individualData.country.country_name } : null,
                state: individualData.state ? { value: individualData.state.state_id.toString(), label: individualData.state.state_name } : null,
                city: individualData.city ? { value: individualData.city.city_id.toString(), label: individualData.city.city_name } : null,
                quota: individualData.quota?.toString() || "",
                tool: selectedTools,
                contact_phone_country: null,
                mobile_number_country: null,
            };
        }
        return baseValues;
    };

    // --- CUSTOM PHONE VALIDATION HELPER ---
    const phoneValidation = (message) => Yup.string()
        .required(message)
        .test('is-valid-phone', 'Invalid phone number', function(value) {
            const { path, createError } = this;
            // Access the corresponding country object stored in values
            const countryKey = `${path}_country`; 
            const country = this.parent[countryKey];

            // If we have country data (user typed or selected), check length
            if (country && country.format && value) {
                // Calculate required length based on dots in format (e.g. +.. ... ....)
                const requiredLength = (country.format.match(/\./g) || []).length;
                
                // If input length is less than required format length
                if (value.length < requiredLength) {
                    return createError({ path, message: 'Invalid phone number' });
                }
            }
            return true;
        });

    // --- Validation Schemas ---
    const validationSchemas = {
        tab1: Yup.object({
            company_name: Yup.string().required("Company name is required"),
            contact_person: Yup.string().required("Contact person name is required"),
            contact_email: Yup.string().email("Invalid email").required("Contact person email is required"),
            contact_phone: phoneValidation("Contact person phone is required"),
            first_name: Yup.string().required("First name is required"),
            last_name: Yup.string().required("Last name is required"),
            email: Yup.string().email("Invalid email").required("Email address is required"),
            mobile_number: phoneValidation("Mobile number is required"),
        }),
        tab2: Yup.object({
            address: Yup.string(),
        }),
        tab3: Yup.object({
            quota: Yup.string().required("Quote is required"),
            tool: Yup.array().min(1, "At least one tool is required").required("Tool is required")
        })
    };

    // --- Handlers ---
    const handleDialogClose = (resetForm) => {
        resetForm();
        setTab("tab1");
        dispatch(clearStates());
        dispatch(clearCities());
        onDialogClose();
    };

    const handlePhoneChange = (fieldName, value, country, setFieldValue, setFieldTouched) => {
        setFieldValue(fieldName, value);
        setFieldValue(`${fieldName}_country`, country);
        setFieldTouched(fieldName, true, false);
    };

    const handleNext = async (values, setTouched, touched) => {
        const currentSchema = validationSchemas[tab];
        try {
            await currentSchema.validate(values, { abortEarly: false });
            const tabOrder = ["tab1", "tab2", "tab3"];
            const currentIndex = tabOrder.indexOf(tab);
            if (currentIndex < tabOrder.length - 1) {
                setTab(tabOrder[currentIndex + 1]);
            }
        } catch (error) {
            const touchedFields = {};
            if (error.inner) {
                error.inner.forEach(err => touchedFields[err.path] = true);
            }
            setTouched({ ...touched, ...touchedFields });
        }
    };

    const handleTabClick = async (targetTab, values, setTouched, touched) => {
        const tabOrder = ["tab1", "tab2", "tab3"];
        const currentIndex = tabOrder.indexOf(tab);
        const targetIndex = tabOrder.indexOf(targetTab);

        if (targetIndex < currentIndex) {
            setTab(targetTab);
            return;
        }

        if (targetIndex > currentIndex + 1) {
             return; 
        }

        const currentSchema = validationSchemas[tab];
        try {
            await currentSchema.validate(values, { abortEarly: false });
            setTab(targetTab);
        } catch (error) {
            const touchedFields = {};
            if (error.inner) {
                error.inner.forEach(err => touchedFields[err.path] = true);
            }
            setTouched({ ...touched, ...touchedFields });
        }
    };

    const handleCountryChange = (selectedOption, setFieldValue) => {
        setFieldValue("country", selectedOption);
        setFieldValue("state", null);
        setFieldValue("city", null);
        dispatch(clearStates());
        dispatch(clearCities());
        if (selectedOption) dispatch(getStateList(selectedOption.value));
    };

    const handleStateChange = (selectedOption, setFieldValue) => {
        setFieldValue("state", selectedOption);
        setFieldValue("city", null);
        dispatch(clearCities());
        if (selectedOption) dispatch(getCityList(selectedOption.value));
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        const addressFull = values.addressLine2
            ? `${values.address}\n${values.addressLine2}`
            : values.address;

        const toolIds = values.tool ? values.tool.map(item => item.value) : [];

        const payload = {
            company_name: values.company_name,
            contact_person: values.contact_person,
            contact_email: values.contact_email,
            contact_phone: values.contact_phone,
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            mobile_number: values.mobile_number,
            address: addressFull,
            pincode: values.pincode,
            quota: Number(values.quota),
            city: values.city ? Number(values.city.value) : "",
            state: values.state ? Number(values.state.value) : "",
            country: values.country ? Number(values.country.value) : "",
            toolIds: toolIds
        };

        try {
            let resultAction;
            if (isEdit && individualData) {
                const companyId = individualData.company_id || individualData._id || individualData.id;
                resultAction = await dispatch(editCompanyAdhoc({ companyId, adhocData: payload }));
            } else {
                resultAction = await dispatch(createCompanyAdhoc(payload));
            }

            if (createCompanyAdhoc.fulfilled.match(resultAction) || editCompanyAdhoc.fulfilled.match(resultAction)) {
                SweetAlertService.showSuccess({
                    title: "Success!",
                    message: isEdit ? "Company updated successfully." : "Company added successfully.",
                });
                handleDialogClose(resetForm);
            } else {
                const errorMessage = resultAction.payload || "Something went wrong";
                SweetAlertService.showError({ title: "Error", message: errorMessage });
            }
        } catch (error) {
            SweetAlertService.showError({ title: "Error", message: "An unexpected error occurred." });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchemas[tab]}
            enableReinitialize={true}
            onSubmit={handleSubmit}
        >
            {({ values, setFieldValue, setFieldError, validateForm, isSubmitting, errors, touched, resetForm, setTouched, setFieldTouched }) => (
                <Dialog
                    isOpen={dialogOpen}
                    onClose={() => handleDialogClose(resetForm)}
                    width={900}
                    onRequestClose={() => handleDialogClose(resetForm)}
                    bodyOpenClassName="overflow-hidden"
                >
                   <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
                        <div className="flex items-center">
                            <span className="h-12 w-12 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 5.85112V16.5339C18 17.3376 17.3383 17.9992 16.5346 17.9992H15.7976C15.8643 17.8069 15.9067 17.5887 15.9067 17.3713V3.38196L16.7269 3.79245C17.5141 4.18565 17.9992 4.97287 17.9992 5.85192L18 5.85112ZM2.09252 17.3713V3.38196L1.27231 3.79245C0.485053 4.18565 0 4.97287 0 5.85192V16.5347C0 17.3384 0.661673 18 1.46541 18H2.20243C2.13571 17.8077 2.09333 17.5895 2.09333 17.3721L2.09252 17.3713ZM10.6738 12.9761H7.32462C7.20924 12.9761 7.11505 13.0703 7.11505 13.1857V17.9992H10.8825V13.1857C10.8825 13.0703 10.7884 12.9761 10.673 12.9761H10.6738ZM14.6508 2.30199V17.3713C14.6508 17.7182 14.3699 17.9992 14.0229 17.9992H12.1392V13.1857C12.1392 12.378 11.4814 11.7203 10.6738 11.7203H7.32462C6.51697 11.7203 5.85921 12.378 5.85921 13.1857V17.9992H3.97546C3.62853 17.9992 3.34755 17.7182 3.34755 17.3713L3.34833 2.30199C3.34833 1.03209 4.38125 0 5.65042 0H12.3479C13.6179 0 14.65 1.03287 14.65 2.30199H14.6508ZM11.3017 8.9992C11.3017 8.65228 11.0207 8.37131 10.6738 8.37131H7.32462C6.97769 8.37131 6.6967 8.65228 6.6967 8.9992C6.6967 9.34611 6.97769 9.62708 7.32462 9.62708H10.6738C11.0207 9.62708 11.3017 9.34611 11.3017 8.9992ZM11.3017 6.48764C11.3017 6.14073 11.0207 5.85975 10.6738 5.85975H7.32462C6.97769 5.85975 6.6967 6.14073 6.6967 6.48764C6.6967 6.83456 6.97769 7.11553 7.32462 7.11553H10.6738C11.0207 7.11553 11.3017 6.83456 11.3017 6.48764ZM11.3017 3.97609C11.3017 3.62917 11.0207 3.3482 10.6738 3.3482H7.32462C6.97769 3.3482 6.6967 3.62917 6.6967 3.97609C6.6967 4.32301 6.97769 4.60398 7.32462 4.60398H10.6738C11.0207 4.60398 11.3017 4.32301 11.3017 3.97609Z" fill="#344054" />
                                </svg>
                            </span>
                            <div>
                                <h5 className="mb-0 text-gray-900 text-base font-semibold">
                                    {isEdit ? "Edit Adhoc Company" : "Add Adhoc Company"}
                                </h5>
                            </div>
                        </div>
                        <button onClick={() => handleDialogClose(resetForm)} className="text-gray-400 hover:text-gray-600">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                    </div>

                    <Form>
                        <Tabs value={tab} variant="pill">
                            <div className="px-6 mb-5">
                                <div className="p-1 rounded-lg client-portal-tab" style={{ backgroundColor: '#FFF4E5' }}>
                                    <TabList>
                                        <TabNav value="tab1" onClick={() => handleTabClick("tab1", values, setTouched, touched)} style={{ backgroundColor: tab === 'tab1' ? '#ffffff' : 'transparent', color: '#344054', fontWeight: tab === 'tab1' ? '600' : '500' }}>Basic Details</TabNav>
                                        <TabNav value="tab2" onClick={() => handleTabClick("tab2", values, setTouched, touched)} style={{ backgroundColor: tab === 'tab2' ? '#ffffff' : 'transparent', color: '#344054', fontWeight: tab === 'tab2' ? '600' : '500' }}>Address Details</TabNav>
                                        <TabNav value="tab3" onClick={() => handleTabClick("tab3", values, setTouched, touched)} style={{ backgroundColor: tab === 'tab3' ? '#ffffff' : 'transparent', color: '#344054', fontWeight: tab === 'tab3' ? '600' : '500' }}>Other Details</TabNav>
                                    </TabList>
                                </div>
                            </div>

                            {/* Tab 1: Basic Details */}
                            <TabContent value="tab1">
                                <div className="px-6">
                                    <div className="flex mb-5">
                                        <label className="font-medium text-gray-700 w-1/4 pt-2">Company Info*</label>
                                        <div className="w-3/4 pl-2 flex gap-3">
                                            <div className="flex-1">
                                                <Field name="company_name" className={`h-10 px-3 w-full input input-shadow text-sm border rounded-lg appearance-none text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${touched.company_name && errors.company_name ? 'border-red-500' : 'border-gray-300'}`} placeholder="Enter Company name here" />
                                                <ErrorMessage name="company_name" component="p" className="text-xs text-red-600 mt-1" />
                                            </div>
                                            <div className="flex-1">
                                                <Field name="contact_person" className={`h-10 px-3 w-full input input-shadow text-sm border rounded-lg appearance-none text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${touched.contact_person && errors.contact_person ? 'border-red-500' : 'border-gray-300'}`} placeholder="Enter Contact person name here" />
                                                <ErrorMessage name="contact_person" component="p" className="text-xs text-red-600 mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex mb-5">
                                        <label className="font-medium text-gray-700 w-1/4 pt-2"></label>
                                        <div className="w-3/4 pl-2 flex gap-3">
                                            <div className="flex-1">
                                                <Field type="email" name="contact_email" className={`h-10 px-3 w-full input input-shadow text-sm border rounded-lg appearance-none text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${touched.contact_email && errors.contact_email ? 'border-red-500' : 'border-gray-300'}`} placeholder="Enter Contact person email here" />
                                                <ErrorMessage name="contact_email" component="p" className="text-xs text-red-600 mt-1" />
                                            </div>
                                            <div className="flex-1">
                                                <CommonPhoneInput 
                                                    value={values.contact_phone} 
                                                    onChange={(phone, country) => handlePhoneChange("contact_phone", phone, country, setFieldValue, setFieldTouched)} 
                                                    onBlur={() => setFieldTouched("contact_phone", true)} 
                                                    placeholder="Enter Contact person phone number here" 
                                                    error={touched.contact_phone && errors.contact_phone} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex mb-5">
                                        <label className="font-medium text-gray-700 w-1/4 pt-2">HR Info*</label>
                                        <div className="w-3/4 pl-2 flex gap-3">
                                            <div className="flex-1">
                                                <Field name="first_name" className={`h-10 px-3 w-full input input-shadow text-sm border rounded-lg appearance-none text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${touched.first_name && errors.first_name ? 'border-red-500' : 'border-gray-300'}`} placeholder="Enter First name here" />
                                                <ErrorMessage name="first_name" component="p" className="text-xs text-red-600 mt-1" />
                                            </div>
                                            <div className="flex-1">
                                                <Field name="last_name" className={`h-10 px-3 w-full input input-shadow text-sm border rounded-lg appearance-none text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${touched.last_name && errors.last_name ? 'border-red-500' : 'border-gray-300'}`} placeholder="Enter Last name here" />
                                                <ErrorMessage name="last_name" component="p" className="text-xs text-red-600 mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex mb-5">
                                        <label className="font-medium text-gray-700 w-1/4 pt-2"></label>
                                        <div className="w-3/4 pl-2 flex gap-3">
                                            <div className="flex-1">
                                                <Field type="email" name="email" className={`h-10 px-3 w-full input input-shadow text-sm border rounded-lg appearance-none text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${touched.email && errors.email ? 'border-red-500' : 'border-gray-300'}`} placeholder="Enter Email address here" />
                                                <ErrorMessage name="email" component="p" className="text-xs text-red-600 mt-1" />
                                            </div>
                                            <div className="flex-1">
                                                <CommonPhoneInput 
                                                    value={values.mobile_number} 
                                                    onChange={(phone, country) => handlePhoneChange("mobile_number", phone, country, setFieldValue, setFieldTouched)} 
                                                    onBlur={() => setFieldTouched("mobile_number", true)} 
                                                    placeholder="Enter Mobile number here" 
                                                    error={touched.mobile_number && errors.mobile_number} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabContent>

                            {/* Tab 2: Address Details */}
                            <TabContent value="tab2">
                                <div className="px-6">
                                     <div className="flex mb-5">
                                        <label className="font-medium text-gray-700 w-1/4 pt-2">Country/State/City</label>
                                        <div className="w-3/4 pl-2 flex gap-3">
                                            <div className="flex-1">
                                                <Select className="w-full h-10 select-options" placeholder="Select Country" options={countryOptions} value={values.country} onChange={(selectedOption) => handleCountryChange(selectedOption, setFieldValue)} />
                                                <ErrorMessage name="country" component="p" className="text-xs text-red-600 mt-1" />
                                            </div>
                                            <div className="flex-1">
                                                <Select className="w-full h-10 select-options" placeholder="State" options={stateOptions} value={values.state} onChange={(selectedOption) => handleStateChange(selectedOption, setFieldValue)} isDisabled={!values.country || stateOptions.length === 0} />
                                                <ErrorMessage name="state" component="p" className="text-xs text-red-600 mt-1" />
                                            </div>
                                            <div className="flex-1">
                                                <Select className="w-full h-10 select-options" placeholder="City" options={cityOptions} value={values.city} onChange={(selectedOption) => setFieldValue("city", selectedOption)} isDisabled={!values.state || cityOptions.length === 0} />
                                                <ErrorMessage name="city" component="p" className="text-xs text-red-600 mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex mb-5">
                                        <label className="font-medium text-gray-700 w-1/4 pt-2">Address</label>
                                        <div className="w-3/4 pl-2 flex flex-col gap-3">
                                            <Field name="address" className="h-10 px-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline" placeholder="Line 1" />
                                            <ErrorMessage name="address" component="p" className="text-xs text-red-600 -mt-2" />
                                            <Field name="addressLine2" className="h-10 px-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline" placeholder="Line 2" />
                                        </div>
                                    </div>
                                   
                                    <div className="flex mb-5">
                                        <label className="font-medium text-gray-700 w-1/4 pt-2">Pincode</label>
                                        <div className="w-3/4 pl-2">
                                            <Field name="pincode" type="text" inputMode="numeric" pattern="[0-9]*" className="h-10 px-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline" placeholder="Pincode" onKeyPress={(e) => { const charCode = e.which ? e.which : e.keyCode; if (charCode > 31 && (charCode < 48 || charCode > 57)) { e.preventDefault(); } }} />
                                        </div>
                                    </div>
                                </div>
                            </TabContent>

                            {/* Tab 3: Other Details */}
                            <TabContent value="tab3">
                                <div className="px-6">
                                    <div className="flex mb-5">
                                        <label className="font-medium text-gray-700 w-1/4 pt-2">Quote*</label>
                                        <div className="w-3/4 pl-2">
                                            <Field name="quota" className={`h-10 px-3 w-full input input-shadow text-sm border rounded-lg appearance-none text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${touched.quota && errors.quota ? 'border-red-500' : 'border-gray-300'}`} placeholder="Enter Quote here" />
                                            <ErrorMessage name="quota" component="p" className="text-xs text-red-600 mt-1" />
                                        </div>
                                    </div>
                                    <div className="flex mb-5">
                                        <label className="font-medium text-gray-700 w-1/4 pt-2">Tool*</label>
                                        <div className="w-3/4 pl-2">
                                            <Select
                                                className={`w-full h-10 select-options ${touched.tool && errors.tool ? 'border-red-500 rounded-md' : ''}`}
                                                placeholder="Tool"
                                                isMulti={true}
                                                options={toolOptions}
                                                value={values.tool}
                                                onChange={(selectedOption) => setFieldValue("tool", selectedOption)}
                                            />
                                            <ErrorMessage name="tool" component="p" className="text-xs text-red-600 mt-1" />
                                        </div>
                                    </div>
                                </div>
                            </TabContent>
                        </Tabs>

                        <div className="flex dailog-footer">
                            <button type="button" className="mr-1.5 whiteBtnShadow btn border border-gray-100 block w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-gray-300 duration-100 secondary-btn" onClick={() => handleDialogClose(resetForm)} style={{ backgroundColor: '#FFF4E5' }}>Cancel</button>

                            {(tab === "tab1" || tab === "tab2") && (
                                <BtnPrimary type="button" onClick={() => handleNext(values, setTouched, touched)} className="ml-1.5 flex items-center justify-center gap-2 px-6 py-2 rounded-lg text-white font-semibold text-[15px]" style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}>
                                    <span className="flex items-center">Next <svg className="ml-2" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.833984 6.66671H12.5007M12.5007 6.66671L6.66732 0.833374M12.5007 6.66671L6.66732 12.5" stroke="white" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                                </BtnPrimary>
                            )}

                            {tab === "tab3" && (
                                <BtnPrimary type="submit" disabled={isSubmitting} className="ml-1.5" style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}>
                                    {isSubmitting ? <ThreeDotLoader /> : (isEdit ? "Update Adhoc Company" : "Add Adhoc Company")}
                                </BtnPrimary>
                            )}
                        </div>
                    </Form>
                </Dialog>
            )}
        </Formik>
    );
};

export default AddEditAdhocCompany;